"use client";

import { useContext, useEffect, useState, useSyncExternalStore } from "react";
import QRCode from "qrcode";
import { ScanFace, ShieldCheck } from "@app-foundation/icons/lucide";
import { FaceVerificationForm } from "@/features/identity/components/FaceVerificationForm";
import type { PreparedFaceIdentity } from "@/features/identity/face-consent";

import { VerificationQrContext } from "./local-view-context";

const subscribe = (notify: () => void) => {
  const query = window.matchMedia("(max-width: 760px)");
  query.addEventListener("change", notify);
  return () => query.removeEventListener("change", notify);
};
const mobileSnapshot = () => window.matchMedia("(max-width: 760px)").matches;
type Handoff = { image: string; expiresAt: number };
async function prepareHandoff(identity: PreparedFaceIdentity, signal?: AbortSignal): Promise<Handoff> {
  const response = await fetch("/api/auth/face/handoff", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(identity), signal });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "二维码生成失败，请重试");
  const image = await QRCode.toDataURL(result.url, { width: 320, margin: 3, errorCorrectionLevel: "M" });
  return { image, expiresAt: result.expiresAt };
}

export function OnboardingFace({ nonce, returning, passed, onVerified }: { showForm?: boolean; nonce?: string; returning: boolean; passed: boolean; onVerified: () => void }) {
  const publishQr = useContext(VerificationQrContext);
  const mobile = useSyncExternalStore(subscribe, mobileSnapshot, () => false);
  const localForm = returning;
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  // Keep the explicitly entered information only in this page's memory for
  // same-device continuation and QR renewal; never put it in browser storage.
  const [identity, setIdentity] = useState<PreparedFaceIdentity>();
  const [message, setMessage] = useState("");
  const [renewing, setRenewing] = useState(false);
  useEffect(() => {
    const available = !passed && !renewing && !message && handoff && handoff.expiresAt * 1000 > Date.now();
    publishQr?.(available ? handoff.image : null);
    const expiry = available ? setTimeout(() => publishQr?.(null), Math.max(0, handoff.expiresAt * 1000 - Date.now())) : undefined;
    return () => { clearTimeout(expiry); publishQr?.(null); };
  }, [handoff, passed, renewing, message, publishQr]);
  useEffect(() => {
    if (mobile || localForm || passed || !handoff) return;
    let active = true;
    const controller = new AbortController();
    const expiry = setTimeout(async () => {
      if (!identity) return;
      setRenewing(true); setMessage("");
      try {
        const result = await prepareHandoff(identity, controller.signal);
        if (active) setHandoff(result);
      } catch (error) { if (active) setMessage(error instanceof Error ? error.message : "二维码生成失败，请重试"); }
      finally { if (active) setRenewing(false); }
    }, Math.max(0, handoff.expiresAt * 1000 - Date.now()));
    return () => { active = false; clearTimeout(expiry); controller.abort(); };
  }, [mobile, localForm, passed, handoff, identity]);
  useEffect(() => {
    if (mobile || localForm || passed || !handoff) return;
    let active = true;
    let checking = false;
    // Poll only the stored result. The phone supplies its own device metadata,
    // while the server fixes the identity to the original device's submission.
    const timer = setInterval(async () => {
      if (!active || checking || document.visibilityState !== "visible") return;
      checking = true;
      try {
        const response = await fetch("/api/auth/face", { cache: "no-store" });
        if (response.ok && (await response.json()).passed === true && active) onVerified();
      } catch { /* Retry on the next polling interval. */ }
      finally { checking = false; }
    }, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [mobile, localForm, passed, onVerified, handoff]);

  if (passed) return <div className="rg-face-qr"><div className="rg-success-icon"><ShieldCheck size={42} aria-hidden="true" /></div><p role="status">实名认证与人脸识别已通过</p></div>;
  if (mobile || localForm || !handoff) return <>
    <FaceVerificationForm nonce={nonce} showBackLink={false} onboarding returning={returning} preparedIdentity={identity} onVerified={onVerified}
      onPrepare={mobile || localForm ? undefined : async fields => {
        const result = await prepareHandoff(fields);
        setIdentity(fields);
        setHandoff(result);
      }} />
  </>;
  const qr = !renewing && !message ? handoff.image : "";
  return <div className="rg-face-qr">
    <div className="rg-face-qr-card">
      {qr ? <img src={qr} width={280} height={280} alt="手机刷脸认证入口二维码" /> /* eslint-disable-line @next/next/no-img-element */ : <ScanFace size={96} strokeWidth={1} aria-hidden="true" />}
      <span>{qr ? "刷脸认证" : message ? "二维码暂不可用" : "正在加载二维码…"}</span>
    </div>
    {message && <><p role="alert" className="rg-error">{message}</p><button type="button" className="rg-secondary" disabled={renewing} onClick={() => setHandoff(current => current ? { ...current, expiresAt: 0 } : current)}>重新获取</button></>}
    <button type="button" className="rg-secondary min-h-11" onClick={() => {
      setHandoff(null); setMessage(""); setRenewing(false);
      setIdentity(undefined);
    }}>重新填写认证信息</button>
  </div>;
}
